// src/panel/ArrivalsWallScreen.tsx
//
// «Pantalla de pared» (mockup 02), route `/#/panel?hotel=<id>`. Distinct from
// the existing token-based, login-less `PanelApp`/`PanelScreen` in this same
// folder (public shareable links, `/#/panel/<token>`, no session at all) —
// this screen is reached by an authenticated receptionist who leaves a
// browser tab open on a reception display, reusing the normal session and
// the Llegadas de hoy data. No rail, no app bar (mounted as a `noLayout`
// CustomRoute in App.tsx), 20 px base type, dark by default.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, ThemeProvider, Typography, createTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { lightTheme } from '@/lightTheme';
import { createAppTheme } from '@/theme/createAppTheme';
import { useArrivals } from '@/hooks/useArrivals';
import { LiveStrip } from '@/features/arrivals/components/LiveStrip';
import { StateChip } from '@/features/reservations/components/StateChip';
import { es, enUS } from 'date-fns/locale';
import { arrivalTime } from '@/features/reservations/lib/reservationFormat';
import type { HotelSettings } from '@/types/reservationState';

const ROWS_PER_PAGE = 8;
const PAGE_INTERVAL_MS = 12_000;

// Header and rows are subgrids of one list grid, so the auto-sized columns
// (time, room, state) take the widest cell of the page and every row lines
// up. Below `sm` the time and reservation columns drop out and the state
// chip moves under the name, so a long chip never squeezes the guest out.
const wallListSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr) auto',
    sm: 'auto minmax(0, 2fr) minmax(0, 1.5fr) auto auto',
  },
  columnGap: 2,
  fontVariantNumeric: 'tabular-nums',
} as const;
const wallRowSx = {
  display: 'grid',
  gridColumn: '1 / -1',
  gridTemplateColumns: 'subgrid',
  alignItems: 'center',
  py: 1.25,
  borderBottom: '1px solid',
  borderColor: 'divider',
} as const;
const timeCellSx = { display: { xs: 'none', sm: 'block' } } as const;
const reservationCellSx = { display: { xs: 'none', sm: 'block' } } as const;
const stateCellSx = { display: 'flex', gridColumn: { xs: '1 / -1', sm: 'auto' }, mt: { xs: 0.5, sm: 0 } } as const;

/** Scale the base theme's typography to a ~20 px body size for a display
 *  read from a few metres away, without hand-tuning every variant. */
const buildWallTheme = (mode: 'dark' | 'light') => {
  const base = mode === 'dark' ? createAppTheme('dark') : lightTheme;
  const scale = 20 / 14; // base theme's body2 ≈ 14px
  const scaledTypography: Record<string, unknown> = {};
  Object.entries(base.typography).forEach(([key, value]) => {
    if (value && typeof value === 'object' && 'fontSize' in (value as Record<string, unknown>)) {
      const v = value as { fontSize?: string };
      const px = v.fontSize && v.fontSize.endsWith('rem') ? parseFloat(v.fontSize) * 16 : undefined;
      scaledTypography[key] = px ? { ...v, fontSize: `${(px * scale) / 16}rem` } : value;
    } else {
      scaledTypography[key] = value;
    }
  });
  return createTheme(base, { typography: scaledTypography });
};

export const ArrivalsWallScreen: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();
  const hotelId = searchParams.get('hotel') ?? undefined;
  const dateLocale = translate('common.locale') === 'en' ? enUS : es;

  const [settings, setSettings] = useState<HotelSettings | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    dataProvider
      .getHotelSettings(hotelId)
      .then(({ data }: { data: HotelSettings }) => {
        if (!cancelled) setSettings(data);
      })
      .catch(() => {
        // Fallback dark theme / full content, per the unit's own instruction.
      });
    return () => {
      cancelled = true;
    };
  }, [dataProvider, hotelId]);

  const wallTheme = settings?.wall.theme ?? 'dark';
  const wallContent = settings?.wall.content ?? 'full';
  const footerCounters = settings?.wall.footerCounters ?? false;

  const { data, consecutiveFailures, secondsSinceUpdate, error } = useArrivals({
    hotelId,
    pendingOnly: false,
    pollMs: 15_000,
  });

  const theme = useMemo(() => buildWallTheme(wallTheme), [wallTheme]);
  const offline = consecutiveFailures >= 2 || error;

  const rows = data?.arrivals ?? [];
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));

  useEffect(() => {
    if (pageCount <= 1) {
      setPage(0);
      return undefined;
    }
    const handle = setInterval(() => setPage((p) => (p + 1) % pageCount), PAGE_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [pageCount]);

  const pageRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
        }}
      >
        {/* Reserve the header's height and ellipsize the facility name (F-A-016):
            a longer real hotel name used to wrap onto a second line at phone
            width, reflowing everything below it instead of staying put. */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, minHeight: 44 }}>
          <Typography variant="h5" fontWeight={800} noWrap sx={{ minWidth: 0 }}>
            {data?.hotels[0]?.name ?? translate('resources.arrivals.name')}
          </Typography>
          <Typography variant="body1" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {offline
              ? translate('wall.offline')
              : translate('wall.updatedAt', { time: new Date(Date.now() - secondsSinceUpdate * 1000).toLocaleTimeString() })}
          </Typography>
        </Box>

        {offline && (
          <Typography variant="body1" sx={{ mb: 2, color: 'error.main' }}>
            {translate('wall.offlineDescription')}
          </Typography>
        )}

        {data && <LiveStrip groups={data.live} dense />}

        {wallContent === 'full' && data && (
          <Box sx={{ mt: 2, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {translate('resources.arrivals.wallList.title', { count: rows.length })}
            </Typography>
            <Box sx={wallListSx}>
              <Box sx={{ ...wallRowSx, py: 0.75, color: 'text.secondary' }}>
                <Typography variant="body2" sx={timeCellSx}>{translate('resources.arrivals.columns.time')}</Typography>
                <Typography variant="body2" noWrap>{translate('resources.arrivals.wallList.guest')}</Typography>
                <Typography variant="body2" noWrap sx={reservationCellSx}>{translate('resources.arrivals.wallList.reservation')}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>{translate('resources.arrivals.columns.room')}</Typography>
                <Typography variant="body2" sx={timeCellSx}>{translate('resources.arrivals.columns.state')}</Typography>
              </Box>
              {pageRows.map((row) => (
                <Box key={row.id} sx={wallRowSx}>
                  <Typography variant="body1" sx={timeCellSx}>{arrivalTime(row, dateLocale)}</Typography>
                  {/* Name and reservation number in their own columns: mixing them in one
                      column left the reader guessing which of the two each row showed. */}
                  <Typography variant="body1" fontWeight={700} noWrap>
                    {row.guestName || (
                      <>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{row.reservationNumber}</Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>—</Box>
                      </>
                    )}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" noWrap sx={reservationCellSx}>
                    {row.reservationNumber}
                  </Typography>
                  {/* Sized to its content: long room numbers («2457335») must never run into the chip. */}
                  <Typography variant="body1" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {row.roomNumber ?? '—'}
                  </Typography>
                  <Box sx={stateCellSx}>
                    <StateChip state={row.state} />
                  </Box>
                </Box>
              ))}
            </Box>
            {pageCount > 1 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'right' }}>
                {translate('wall.page', { page: page + 1, total: pageCount })}
              </Typography>
            )}
          </Box>
        )}

        {footerCounters && data && (
          <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body1">{translate('resources.arrivals.counters.arrivals')}: {data.counters.arrivals}</Typography>
            <Typography variant="body1">{translate('resources.arrivals.counters.ready')}: {data.counters.ready}</Typography>
            <Typography variant="body1">{translate('resources.arrivals.counters.checkedIn')}: {data.counters.checkedIn}</Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ArrivalsWallScreen;
